import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { CloseIcon } from '@library';
import MultiSelect from '../../../../core/components/input/Select/MultiSelect';
import type { SelectDropdownOptions } from '../../../../core/components/input/Select/SelectDropdown';
import HorizontalLayout from '../../../../core/components/layout/HorizontalLayout';
import VerticalLayout from '../../../../core/components/layout/VerticalLayout';
import type { VariantFilters } from '../../../types';
import styles from './ProductBulkOrder.module.scss';

interface FilterOptionsMap {
    [name: string]: SelectDropdownOptions[];
}

const ProductBulkOrderControls = memo(
    ({
        filters,
        filterOptions,
        showVariantsFilter,
        showSiblingFilter,
        setSiblingAndVariantFilters,
    }: {
        filters: VariantFilters;
        filterOptions: FilterOptionsMap;
        showVariantsFilter: boolean;
        showSiblingFilter: boolean;
        setSiblingAndVariantFilters: React.Dispatch<
            React.SetStateAction<{
                [name: string]: {
                    type: string;
                    value: string[];
                };
            }>
        >;
    }) => {
        const siblingFilters = Object.entries(filters)
            .filter(([, { type }]) => type === 'sibling')
            .map(([name, { value }]) => ({ name, value }));

        const variantFilters = Object.entries(filters)
            .filter(([, { type }]) => type === 'variant')
            .map(([name, { value }]) => ({ name, value }));

        return (
            <>
                {siblingFilters.map(({ name, value }) => (
                    <MultiSelect
                        key={name}
                        fullWidth
                        disabled={!showSiblingFilter}
                        bordered
                        keepLabel
                        menuSize="full"
                        data-test-class="productVariantsTableFilter"
                        data-test-state={name}
                        placeholder={name}
                        options={filterOptions[name]}
                        value={value}
                        onChange={newValue =>
                            setSiblingAndVariantFilters(currentFilters => ({
                                ...currentFilters,
                                [name]: {
                                    type: 'sibling',
                                    value: newValue,
                                },
                            }))
                        }
                    />
                ))}

                {variantFilters.map(({ name, value }) => (
                    <MultiSelect
                        key={name}
                        fullWidth
                        disabled={!showVariantsFilter}
                        bordered
                        menuSize="full"
                        data-test-id="non-searchable-multiSelect"
                        data-test-class="productVariantsTableFilter"
                        data-test-state={name}
                        placeholder={name}
                        options={filterOptions[name]}
                        keepLabel
                        value={value}
                        onChange={newValue =>
                            setSiblingAndVariantFilters(currentFilters => ({
                                ...currentFilters,
                                [name]: {
                                    type: 'variant',
                                    value: newValue,
                                },
                            }))
                        }
                    />
                ))}
            </>
        );
    }
);

const ProductBulkOrderFilterControls = ({
    areFiltersActive,
    totalFiltersCount,
    filters,
    filterOptions,
    showSiblingFilter,
    showVariantsFilter,
    setSiblingAndVariantFilters,
}: {
    areFiltersActive: boolean;
    totalFiltersCount: number;
    filters: VariantFilters;
    filterOptions: FilterOptionsMap;
    showSiblingFilter: boolean;
    showVariantsFilter: boolean;
    setSiblingAndVariantFilters: React.Dispatch<
        React.SetStateAction<{
            [name: string]: {
                type: string;
                value: string[];
            };
        }>
    >;
}) => {
    const resetFilters = () => {
        const resetValue = Object.fromEntries(
            Object.entries(filters)
                .filter(([name]) => name !== 'availability')
                .map(([name, { type }]) => [name, { type, value: [] }])
        );

        setSiblingAndVariantFilters(resetValue);
    };

    return (
        <VerticalLayout
            gap={areFiltersActive ? 'small' : 'large'}
            stretchChildren
        >
            <HorizontalLayout gap="small" justify="spaceBetween" wrap>
                <div
                    className={styles.filterSelects}
                    style={{
                        maxWidth: 350 * totalFiltersCount,
                    }}
                >
                    <HorizontalLayout gap="small">
                        <ProductBulkOrderControls
                            filters={filters}
                            filterOptions={filterOptions}
                            showVariantsFilter={showVariantsFilter}
                            showSiblingFilter={showSiblingFilter}
                            setSiblingAndVariantFilters={
                                setSiblingAndVariantFilters
                            }
                        />
                    </HorizontalLayout>
                </div>
            </HorizontalLayout>
            {areFiltersActive && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={resetFilters}
                    onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                            resetFilters();
                        }
                    }}
                    className={styles.clearFiltersButton}
                >
                    <HorizontalLayout gap="micro" align="center">
                        <CloseIcon />
                        <span>
                            <FormattedMessage id="reset filter" />
                        </span>
                    </HorizontalLayout>
                </div>
            )}
        </VerticalLayout>
    );
};

export default memo(ProductBulkOrderFilterControls);
